'use client'

import * as React from 'react'
import { ScrollArea } from '~/sotano-kit/primitives/scroll-area'

import { DocsTree } from './docs-tree'
import { parseTab, useTabsStore } from '../tabs/store'
import { FavsTree } from './favs-tree'
import { useRepoStore } from '../repo-store'
import { clsx } from 'clsx'
import s from './tab.module.scss'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { useStorage } from '~/hooks/use-storage'
import { MagnifyingGlassIcon } from '@radix-ui/react-icons'
import { CommandPaletteTrigger } from '~/components/command-palette'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'
import { isWindows } from 'react-device-detect'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { getId } from '@basehub/utils/id/index'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'
import { isHotkey } from 'is-hotkey'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { toast } from 'sonner'
import { Block } from '@basehub/data/replicache/index'
import { useIsMemberOrHigher } from '~/hooks-data/use-user-role'
import { useUserPreferences } from '~/hooks-data/use-user-preferences'
import { EditorModeProvider } from '../editor-mode-provider'

export const ExplorerTab = React.forwardRef<HTMLDivElement>(
  (_, forwardedRef) => {
    const addTab = useTabsStore((s) => s.addTab)
    const parsedActiveTab = useTabsStore((s) =>
      s.activeTab ? parseTab(s.activeTab) : null
    )
    const workingTree = useWorkingTreeStore((s) => s.tree)
    const treeManagerRef = useWorkingTreeStore((s) => s.treeRef)
    const getPath = useWorkingTreeStore((s) => s.tree?.getPath)
    const replicache = useRepoReplicache().data?.replicache

    const isMemberOrHigher = useIsMemberOrHigher()
    const editModeIsEnabled = useUserPreferences((s) => s.editMode)
    // returns true if block can be deleted
    const canAlterBlockSchema = React.useCallback(
      ({
        parent,
      }: {
        parent: Block | null
      }): {
        value: boolean
        reason?: string
      } => {
        if (parent?.type === 'instance') {
          return { value: false, reason: `Can't edit instance blocks' schema` }
        }
        if (isMemberOrHigher) {
          if (editModeIsEnabled) {
            return {
              value: false,
              reason: `Disable Edit Mode to edit blocks' schema`,
            }
          }
        } else {
          return {
            value: false,
            reason: `You do not have permission to edit blocks' schema`,
          }
        }

        return { value: true }
      },
      [editModeIsEnabled, isMemberOrHigher]
    )

    const [explorerTabVisible, setExplorerTabVisible] = useStorage(
      'sessionStorage',
      (z) => z.boolean(),
      `basehub-sidebar-tab-visible`,
      { initialValue: false }
    )

    React.useEffect(() => {
      if (!workingTree) return

      const beforeunload = () => setExplorerTabVisible(false)

      setExplorerTabVisible(true)

      window.addEventListener('beforeunload', beforeunload)

      return () => {
        window.removeEventListener('beforeunload', beforeunload)
      }
    }, [setExplorerTabVisible, workingTree])

    const setFocus = useRepoStore((s) => s.setFocus)

    const activeKey =
      parsedActiveTab && 'blockId' in parsedActiveTab
        ? parsedActiveTab.blockId
        : ''
    const autoFocusKey = activeKey

    const [_docsTreeSelectedKeys, setDocsTreeSelectedKeys] = React.useState<
      Set<{ label: string; id: string }>
    >(new Set())
    const [_favsTreeSelectedKeys, setFavsTreeSelectedKeys] = React.useState<
      Set<{ label: string; id: string }>
    >(new Set())

    const docsTreeSelectedKeys: typeof _docsTreeSelectedKeys =
      React.useMemo(() => {
        if (!workingTree) return new Set()
        try {
          return new Set(
            [..._docsTreeSelectedKeys].map((s) => {
              return {
                ...s,
                id: idToSourceBlockId(s.id, workingTree),
              }
            })
          )
        } catch (e) {
          return new Set()
        }
      }, [_docsTreeSelectedKeys, workingTree])

    const favsTreeSelectedKeys: typeof _favsTreeSelectedKeys =
      React.useMemo(() => {
        if (!workingTree) return new Set()
        try {
          return new Set(
            [..._favsTreeSelectedKeys].map((s) => {
              return {
                ...s,
                id: idToSourceBlockId(s.id, workingTree),
              }
            })
          )
        } catch (e) {
          return new Set()
        }
      }, [_favsTreeSelectedKeys, workingTree])

    const handleActiveKeyChange = React.useCallback(
      (key: string, opts: { autoFocus: boolean; partiallyOpen: boolean }) => {
        if (!workingTree) return
        addTab({ key: key, partiallyOpen: opts.partiallyOpen })
        if (opts.autoFocus) {
          setFocus({
            type: 'block:sidebar',
            blockId: idToSourceBlockId(key, workingTree),
          })
        }
      },
      [addTab, setFocus, workingTree]
    )

    const handleKeyDown = React.useCallback(
      async (e: KeyboardEvent) => {
        if (isHotkey('meta+backspace', e)) {
          if (!replicache || !getPath || !treeManagerRef.current) return
          const idsToRemove = Array.from(
            docsTreeSelectedKeys.size
              ? docsTreeSelectedKeys
              : favsTreeSelectedKeys
          ).map((b) => b.id)
          if (idsToRemove.length === 0) {
            const selectedBlockId = document.activeElement?.getAttribute(
              'data-tree-list-item'
            )
            if (!selectedBlockId) return
            idsToRemove.push(selectedBlockId)
          }

          for (const id of idsToRemove) {
            const selectedNode = treeManagerRef.current.getNode(id, {
              fallbackKey: 'sourceBlockId',
              required: true,
            })

            const parentNode = selectedNode.parentKey
              ? treeManagerRef.current.getNode(selectedNode.parentKey, {
                  fallbackKey: 'sourceBlockId',
                  required: true,
                })
              : null

            if (
              selectedNode.value.type !== 'instance' ||
              ['list', 'union'].includes(parentNode?.value.type ?? '') === false
            ) {
              const access = canAlterBlockSchema({
                parent: parentNode?.value ?? null,
              })

              if (!access.value) {
                toast.error(access.reason)
                return
              }
            }
          }

          const nextOrFirstTreeItem =
            document.querySelector(
              `[data-tree-list-child="${idsToRemove.at(
                -1
              )}"] + [data-tree-list-child] button[data-tree-list-item]`
            ) ??
            document.querySelector(
              `button[data-tree-list-item]${idsToRemove
                .map((id) => `:not([data-tree-list-item="${id}"])`)
                .join('')}`
            )

          e.preventDefault()

          await replicache?.mutate
            .deleteBlocks({
              rootId: treeManagerRef.current.root.key,
              blockIds: idsToRemove,
              idSeed: getId(),
              isoNow: new Date().toISOString(),
            })
            .catch((err) => {
              if (err instanceof MutatorUserError) {
                toast.error(err.reason)
              } else {
                console.error(err)
                toast.error('An error occurred while deleting blocks')
              }
            })
            .then(() => {
              toast.success('Blocks deleted')
              if (nextOrFirstTreeItem instanceof HTMLElement) {
                nextOrFirstTreeItem.focus()
              }
            })
        }
      },
      [
        canAlterBlockSchema,
        docsTreeSelectedKeys,
        favsTreeSelectedKeys,
        getPath,
        replicache,
        treeManagerRef,
      ]
    )

    React.useEffect(() => {
      window.addEventListener('keydown', handleKeyDown)

      return () => {
        window.removeEventListener('keydown', handleKeyDown)
      }
    }, [handleKeyDown])

    return (
      <>
        <div className="flex items-center justify-between px-4 mt-3 mb-[7px]">
          <h5 className="text-dark-dim text-sm font-medium">Explore</h5>
          <SimpleTooltip
            content={isWindows ? 'Search (Ctrl + K)' : 'Search (⌘ + K)'}
          >
            <div>
              <CommandPaletteTrigger>
                <button className="outline-1 outline-dark-control-base px-1 py-0.5 leading-none rounded-md flex items-center gap-0.5 text-dark-muted text-xs hover:text-dark-dim transition cursor-default">
                  <MagnifyingGlassIcon />
                </button>
              </CommandPaletteTrigger>
            </div>
          </SimpleTooltip>
        </div>
        <hr className="border-dark-border-solid mx-4" />
        <div
          className={clsx(
            explorerTabVisible && 'opacity-100',
            !workingTree && 'opacity-0',
            'flex-1 overflow-hidden relative transition-opacity duration-200 ease-out'
          )}
        >
          <ScrollArea
            ref={forwardedRef}
            className={clsx(
              'flex-grow-1 flex h-full flex-col overflow-auto',
              s.scrollAreaContainer
            )}
          >
            {workingTree && (
              <EditorModeProvider parentBlock={null} block={null}>
                <FavsTree
                  blockTreeManager={workingTree}
                  activeKey={{ label: '', id: activeKey }}
                  onActiveKeyChange={handleActiveKeyChange}
                  selectedKeys={favsTreeSelectedKeys}
                  setSelectedKeys={setFavsTreeSelectedKeys}
                  onUnselectDocsTree={() => {
                    setDocsTreeSelectedKeys(new Set())
                  }}
                />
                <DocsTree
                  className="flex-grow"
                  blockTreeManager={workingTree}
                  activeKey={{ label: '', id: activeKey }}
                  onActiveKeyChange={handleActiveKeyChange}
                  selectedKeys={docsTreeSelectedKeys}
                  setSelectedKeys={setDocsTreeSelectedKeys}
                  autoFocusKey={autoFocusKey}
                  onAutoFocusKeyFocus={() => undefined}
                  onUnselectFavsTree={() => {
                    setFavsTreeSelectedKeys(new Set())
                  }}
                  onCreateNewBlock={({ blockId }) => {
                    addTab({
                      key: blockId,
                      partiallyOpen: false,
                    })
                    setFocus({ type: 'block:document-title', blockId })
                  }}
                />
              </EditorModeProvider>
            )}
          </ScrollArea>
        </div>
      </>
    )
  }
)
